import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, ElementRef, OnDestroy, output, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import jsQR from "jsqr";
import { Guid } from "moo-shared";
import { catchError, EMPTY, from, Observable, of, startWith, Subject, Subscription, switchMap, tap, timer } from "rxjs";
import { IQrCodeValidatedDto } from "../../../core/authentication/authentication-api.service";
import { AuthenticationErrors } from "../../../core/authentication/authentication-errors";
import { AuthenticationServerService } from "../../../core/authentication/authentication-server.service";
import { IHttpErrorResponseBody } from "../../../core/http-error-response-body.model";
import { BaseComponent } from "../../../shared/base.component";
import { NotificationBarComponent } from "../../../shared/notification-bar";
import { LoginAssistantService } from "../../login-assistant/login-assistant.service";
import { LoginPictureModel } from "../picture-login/login-picture.viewmodel";
import { ChromeDeviceService } from "../picture-login/chrome-device-service";

export interface QrCredentials {
  qrCodeKey: Guid,
  qrVersionKey: Guid;
}

export interface PictureLoginRequired {
  qrCredentials: QrCredentials,
  loginName: string,
  passwordPictures: LoginPictureModel[]
}

@Component({
  selector: "app-qr-login",
  templateUrl: "./qr-login.component.html",
  styleUrls: ["./qr-login.component.scss"]
})
export class QrLoginComponent extends BaseComponent implements AfterViewInit, OnDestroy {
  @ViewChild("video") videoElement!: ElementRef;
  @ViewChild("canvas") canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild("notificationBar") notificationBar!: NotificationBarComponent;

  tokenAcquired = output<string>();
  pictureLoginRequired = output<PictureLoginRequired>();

  stream: MediaStream | null = null;

  reset$ = new Subject();
  timerSubscription: Observable<number> | undefined;

  override componentTranslationRoot = "login.qr-login";

  private cameraSubscription!: Subscription;
  private scanning = true;
  private waitFor: number = 60000;

  qrResultRef = jsQR;
  errorState = false;

  constructor(
    translateService: TranslateService,
    private readonly router: Router,
    private readonly authenticationServerService: AuthenticationServerService,
    private readonly chromeDeviceService: ChromeDeviceService,
    private readonly loginAssistantService: LoginAssistantService
  ) {
    super(translateService);
    this.setTimer();
  }

  ngAfterViewInit(): void {
    this.disableRightClickOnVideo();
    this.openCamera();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stopCamera();
    this.cameraSubscription.unsubscribe();
    this.resetTimer();
  }

  private disableRightClickOnVideo(): void {
    document.getElementsByTagName("video")[0].addEventListener("contextmenu", function (event) {
      event.preventDefault();
    });
  }

  private openCamera() {
    this.cameraSubscription = from(navigator.mediaDevices.getUserMedia({ video: true }))
      .pipe(
        tap(stream => {
          this.stream = stream;
          this.videoElement.nativeElement.srcObject = stream;

          this.scanQrCode();
        }),
        switchMap(() => this.setTimer()),
        tap(() => this.close()),
        catchError((err) => {
          this.showError("no-camera");
          return of(null);
        })
      ).subscribe();
  }

  private setTimer(): Observable<any> {
    return this.timerSubscription = this.reset$.pipe(startWith(void 0), switchMap(() => timer(this.waitFor)));
  }

  private resetTimer() {
    this.reset$.next(void 0);
  }

  private scanQrCode() {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    if (!ctx) {
      return;
    }

    canvas.width = 500;
    canvas.height = 500;

    const scan = () => {
      setTimeout(() => {
        if (!this.scanning) {
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrCode = this.qrResultRef(imageData.data, imageData.width, imageData.height);

        if (qrCode && this.isQrCodeFormatValid(qrCode.data)) {
          this.resetTimer();
          this.parseQrCode(qrCode.data);
          this.scanning = false;
          this.stopCamera();
        } else {
          requestAnimationFrame(scan);
        }
      }, 500);
    };

    scan();
  }

  private isQrCodeFormatValid(qrCode: string): boolean {
    try {
      const q = JSON.parse(qrCode);
      if (!q.qrGuid || !q.qrKey) {
        this.applyErrorState();
        this.showError("invalid");
        return false;
      }

      return true;
    }
    catch (_) {
      this.applyErrorState();
      this.showError("invalid");

      return false;
    }
  }

  private applyErrorState(): void {
    this.errorState = true;
    this.scanning = false;
    this.stopCamera();
    this.cameraSubscription.unsubscribe();
  }

  retryScan(): void {
    this.errorState = false;
    this.notificationBar.hide();
    this.openCamera();
    this.scanning = true;
  }

  private parseQrCode(qrCode: string): void {
    const q = JSON.parse(qrCode);
    this.subscriptionsToRelease.push(this.validateCredentials(q.qrKey, q.qrGuid).subscribe());
  }

  private validateCredentials(qrCodeKey: Guid, qrCodeVersionKey: Guid): Observable<void> {
    return this.authenticationServerService.authenticateUsingQrCode(qrCodeKey, qrCodeVersionKey).pipe(
      switchMap(x => {
        const validatedDto = x as IQrCodeValidatedDto;
        if (validatedDto?.PasswordPictures?.length > 0) {
          this.requirePictureLogin(validatedDto, qrCodeKey, qrCodeVersionKey);
          return EMPTY;
        }
        return this.handOffToken(x.LoginName, x.Token as string);
      }),
      catchError((error) => {
        this.applyErrorState();

        this.showError(this.getErrorMessageTranslationKey(error));

        return EMPTY;
      }));
  }

  private requirePictureLogin(dto: IQrCodeValidatedDto, qrCodeKey: Guid, qrCodeVersionKey: Guid): void {
    this.pictureLoginRequired.emit({
      loginName: dto.LoginName,
      qrCredentials: {
        qrCodeKey: qrCodeKey,
        qrVersionKey: qrCodeVersionKey
      } as QrCredentials,
      passwordPictures: dto.PasswordPictures.map(x => new LoginPictureModel(x.Key, x.Name))
    } as PictureLoginRequired);
  }

  private handOffToken(loginName: string, token: string): Observable<void> {
    this.chromeDeviceService.suppressGoogleLoginForm(loginName);
    this.chromeDeviceService.completeRegistration();
    // Unlike picture-login (which surfaces a missing complete() ack as an error), QR has no
    // notification surface at hand-off time. waitForChromeHandoff() is bounded and swallows a
    // late/absent ack rather than blocking the login.
    return this.chromeDeviceService.waitForChromeHandoff().pipe(
      tap(() => this.tokenAcquired.emit(token)));
  }

  private getErrorMessageTranslationKey(response: HttpErrorResponse): string {
    const responseBody = response.error as IHttpErrorResponseBody;
    return AuthenticationErrors.getErrorTranslationKeyForPictureOrQrLogin(responseBody?.Code);
  }

  private showError(errorKey: string): void {
    this.notificationBar.showErrorMessage({
      icon: "icon-meh",
      text: this.translate(`error.${errorKey}`)
    });
  }

  private stopCamera(): void {
    this.scanning = false;
    this.stream?.getTracks().forEach(track => track.stop());
  }

  close(): void {
    this.loginAssistantService.redirectToLastAssistantScreen();
  }
}
